import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PackageIcon, CheckCircleIcon, XCircleIcon, ArrowLeftIcon, StoreIcon, RefreshCwIcon, TruckIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CONFIG } from '../data/config';
import { Order, OrderStatus, STATUS_CONFIG } from '../admin/types';
import { orderHasItemsForStation } from '../admin/StationConfig';
import type { Station } from '../admin/StationConfig';
interface OrderTrackingProps {
  token: string;
}
interface StationInfo {
  id: Station;
  label: string;
  icon: string;
  status: OrderStatus;
}
const STATUS_WEIGHT: Record<string, number> = {
  new: 0,
  accepted: 20,
  preparing: 50,
  ready: 90,
  out_for_delivery: 95,
  delivered: 100,
  cancelled: 0
};
const STATION_STATUS_LABELS: Record<string, string> = {
  new: 'Pendiente',
  accepted: 'Aceptado',
  preparing: 'Preparando',
  ready: 'Listo',
  delivered: 'Entregado',
  out_for_delivery: 'En camino',
  cancelled: 'Cancelado'
};
const STATION_META: Record<string, {
  label: string;
  icon: string;
}> = {
  cocina: {
    label: 'Cocina',
    icon: '🔥'
  },
  pizzeria: {
    label: 'Pizzería',
    icon: '🍕'
  },
  bebidas: {
    label: 'Bebidas',
    icon: '🥤'
  }
};
function getProgressMessage(pct: number, orderStatus: OrderStatus): string {
  if (orderStatus === 'cancelled') return 'Pedido cancelado';
  if (orderStatus === 'delivered') return '¡Entregado! Que lo disfrutes 🎉';
  if (orderStatus === 'out_for_delivery') return 'Tu pedido va en camino 🛵';
  if (pct >= 90) return '¡Tu pedido está listo! 🎉';
  if (pct >= 61) return '¡Casi listo! 🔥';
  if (pct >= 26) return 'En preparación...';
  if (pct >= 11) return 'Aceptado por el restaurante ✅';
  return 'Tu pedido fue recibido 📋';
}
function getStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'accepted':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/25';
    case 'preparing':
      return 'text-[#F2C94C] bg-[#F2C94C]/10 border-[#F2C94C]/25';
    case 'ready':
      return 'text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/25';
    case 'delivered':
      return 'text-gray-400 bg-gray-400/10 border-gray-400/25';
    case 'out_for_delivery':
      return 'text-[#7A5CFF] bg-[#7A5CFF]/10 border-[#7A5CFF]/25';
    case 'cancelled':
      return 'text-red-400 bg-red-400/10 border-red-400/25';
    default:
      return 'text-gray-500 bg-gray-500/10 border-gray-500/25';
  }
}
function getProgressColor(pct: number): string {
  if (pct >= 90) return '#22C55E';
  if (pct >= 50) return '#F2C94C';
  if (pct >= 20) return '#3B82F6';
  return '#6B7280';
}
export function OrderTracking({
  token
}: OrderTrackingProps) {
  const [order, setOrder] = useState<(Order & {
    order_text?: string;
  }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [stationStatuses, setStationStatuses] = useState<Record<string, OrderStatus>>({});
  // Read station statuses from localStorage
  const readStationStatuses = useCallback(() => {
    try {
      const raw = localStorage.getItem('kds_station_statuses');
      if (raw) {
        setStationStatuses(JSON.parse(raw));
      }
    } catch (e) {}
  }, []);
  // Find order in localStorage
  const findLocalOrder = useCallback((): (Order & {
    order_text?: string;
  }) | null => {
    try {
      const raw = localStorage.getItem('kds_local_orders');
      if (!raw) return null;
      const localOrders = JSON.parse(raw) as any[];
      const found = localOrders.find((o: any) => o.tracking_token === token);
      if (found) {
        return {
          id: found.id,
          order_number: found.order_number,
          status: found.status,
          delivery_type: found.delivery_type,
          created_at: found.created_at,
          total_final: found.total_final,
          order_text: found.order_text || ''
        } as Order & {
          order_text?: string;
        };
      }
    } catch (e) {}
    return null;
  }, [token]);
  const fetchOrder = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    readStationStatuses();
    try {
      const {
        data,
        error: fetchError
      } = await supabase.from('orders').select('id, order_number, status, delivery_type, created_at, total_final, order_text').eq('tracking_token', token).single();
      if (fetchError) throw fetchError;
      if (data) {
        setOrder(data as Order & {
          order_text?: string;
        });
        setLastUpdated(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching order from Supabase:', err);
      const localOrder = findLocalOrder();
      if (localOrder) {
        setOrder(localOrder);
        setLastUpdated(new Date());
        setError(null);
      } else if (!order) {
        setError('Pedido no encontrado');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, order, findLocalOrder, readStationStatuses]);
  useEffect(() => {
    fetchOrder();
    const channel = supabase.channel('order-tracking').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'orders'
    }, payload => {
      if (payload.new.tracking_token === token) {
        setOrder(prev => ({
          ...prev,
          order_number: payload.new.order_number,
          status: payload.new.status,
          delivery_type: payload.new.delivery_type,
          created_at: payload.new.created_at,
          total_final: payload.new.total_final,
          order_text: payload.new.order_text || prev?.order_text || ''
        }) as Order & {
          order_text?: string;
        });
        setLastUpdated(new Date());
      }
    }).subscribe();
    const interval = setInterval(() => fetchOrder(), 5000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [token]);
  // Determine involved stations and their statuses
  const involvedStations = useMemo((): StationInfo[] => {
    if (!order?.order_text) return [];
    const stations: StationInfo[] = [];
    const stationKeys: Station[] = ['cocina', 'pizzeria', 'bebidas'];
    stationKeys.forEach(st => {
      if (orderHasItemsForStation(order.order_text || '', st)) {
        const stKey = `${order.id}_${st}`;
        const stationStatus = stationStatuses[stKey] || 'new';
        const meta = STATION_META[st];
        stations.push({
          id: st,
          label: meta.label,
          icon: meta.icon,
          status: stationStatus as OrderStatus
        });
      }
    });
    return stations;
  }, [order, stationStatuses]);
  // Calculate overall progress
  const progressPct = useMemo(() => {
    if (!order) return 0;
    if (order.status === 'cancelled') return 0;
    if (order.status === 'delivered') return 100;
    if (order.status === 'out_for_delivery') return 95;
    if (order.status === 'new') return 0;
    // If no stations detected, use main order status weight
    if (involvedStations.length === 0) {
      return STATUS_WEIGHT[order.status] || 0;
    }
    // Average of all involved station weights
    const total = involvedStations.reduce((sum, st) => {
      return sum + (STATUS_WEIGHT[st.status] || 0);
    }, 0);
    return Math.round(total / involvedStations.length);
  }, [order, involvedStations]);
  const progressColor = getProgressColor(progressPct);
  if (loading) {
    return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-4" data-id="element-1409">
        <div className="w-8 h-8 border-4 border-dorado/30 border-t-dorado rounded-full animate-spin mb-4" data-id="element-1410" />
        <p className="text-gray-400 font-medium" data-id="element-1411">Buscando tu pedido...</p>
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-4 text-center" data-id="element-1412">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20" data-id="element-1413">
          <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-1414" />
        </div>
        <h1 className="font-heading text-2xl text-white mb-2" data-id="element-1415">
          Pedido no encontrado
        </h1>
        <p className="text-gray-400 mb-8 max-w-xs" data-id="element-1416">
          No pudimos encontrar un pedido con este código de seguimiento.
        </p>
        <button onClick={() => window.location.href = '/'} className="px-6 py-3 bg-surface border border-gray-700 rounded-xl text-white font-medium hover:bg-surface-light transition-colors flex items-center gap-2" data-id="element-1417">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1418" />
          Volver al menú
        </button>
      </div>;
  }
  const isCancelled = order.status === 'cancelled';
  const isDelivered = order.status === 'delivered';
  // SVG circle progress
  const circleRadius = 70;
  const circumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset = circumference - progressPct / 100 * circumference;
  return <div className="min-h-screen bg-carbon w-full flex justify-center" data-id="element-1419">
      <div className="w-full max-w-md bg-carbon min-h-screen relative shadow-2xl overflow-x-hidden flex flex-col" data-id="element-1420">
        {/* Header */}
        <header className="bg-surface border-b border-gray-800 sticky top-0 z-30 px-4 py-3" data-id="element-1421">
          <div className="flex items-center justify-between" data-id="element-1422">
            <div className="flex items-center gap-3" data-id="element-1423">
              <div className="w-10 h-10 bg-dorado/10 rounded-xl flex items-center justify-center border border-dorado/20" data-id="element-1424">
                <PackageIcon className="w-5 h-5 text-dorado" data-id="element-1425" />
              </div>
              <div data-id="element-1426">
                <h1 className="font-heading text-lg text-white leading-tight" data-id="element-1427">
                  {CONFIG.APP_NAME}
                </h1>
                <p className="text-xs text-gray-400" data-id="element-1428">Seguimiento de pedido</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1429">
              <button onClick={() => fetchOrder(true)} disabled={refreshing} className="p-2 bg-surface-light border border-gray-700 rounded-lg text-gray-400 hover:text-dorado hover:border-dorado/30 transition-colors active:scale-95" data-id="element-1430">
                <RefreshCwIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} data-id="element-1431" />
              </button>
              <button onClick={() => window.location.href = '/'} className="text-xs font-medium text-gray-400 hover:text-white transition-colors flex items-center gap-1 bg-surface-light px-3 py-1.5 rounded-lg border border-gray-700" data-id="element-1432">
                Ver menú
              </button>
            </div>
          </div>
          {lastUpdated && <p className="text-[10px] text-gray-600 text-right mt-1" data-id="element-1433">
              Actualizado:{' '}
              {lastUpdated.toLocaleTimeString('es-CR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
            </p>}
        </header>

        <main className="flex-1 p-4 space-y-5" data-id="element-1434">
          {/* Order Number + Status */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-surface border border-gray-800 rounded-2xl p-5 shadow-lg" data-id="element-1435">
            <div className="flex justify-between items-start mb-4" data-id="element-1436">
              <div data-id="element-1437">
                <p className="text-xs text-gray-500 mb-1" data-id="element-1438">Orden</p>
                <h2 className="font-heading text-2xl text-white tracking-wider" data-id="element-1439">
                  #{order.order_number}
                </h2>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_CONFIG[order.status].bg} ${STATUS_CONFIG[order.status].color}`} data-id="element-1440">
                {STATUS_CONFIG[order.status].label}
              </div>
            </div>
            <div className="flex justify-between text-sm border-t border-gray-800 pt-3" data-id="element-1441">
              <span className="text-gray-400" data-id="element-1442">Entrega</span>
              <span className="text-white font-medium flex items-center gap-1.5" data-id="element-1443">
                {order.delivery_type === 'express' ? <>
                    Express <TruckIcon className="w-3.5 h-3.5 text-dorado" data-id="element-1444" />
                  </> : <>
                    Retiro <StoreIcon className="w-3.5 h-3.5 text-dorado" data-id="element-1445" />
                  </>}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-2" data-id="element-1446">
              <span className="text-gray-400" data-id="element-1447">Total</span>
              <span className="text-white font-bold" data-id="element-1448">
                {CONFIG.CURRENCY_SYMBOL}
                {order.total_final?.toLocaleString()}
              </span>
            </div>
          </motion.div>

          {/* Progress Circle */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-surface border border-gray-800 rounded-2xl p-6 shadow-lg flex flex-col items-center" data-id="element-1449">
            {isCancelled ? <div className="flex flex-col items-center text-center py-4" data-id="element-1450">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20" data-id="element-1451">
                  <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-1452" />
                </div>
                <h4 className="text-red-400 font-bold text-lg mb-2" data-id="element-1453">
                  Pedido Cancelado
                </h4>
                <p className="text-gray-400 text-sm" data-id="element-1454">
                  Este pedido ha sido cancelado. Si tienes dudas, contáctanos al
                  WhatsApp.
                </p>
              </div> : <>
                {/* Circular Progress */}
                <div className="relative w-44 h-44 mb-4" data-id="element-1455">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160" data-id="element-1456">
                    {/* Background circle */}
                    <circle cx="80" cy="80" r={circleRadius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" data-id="element-1457" />
                    {/* Progress circle */}
                    <motion.circle cx="80" cy="80" r={circleRadius} fill="none" stroke={progressColor} strokeWidth="10" strokeLinecap="round" strokeDasharray={circumference} initial={{
                  strokeDashoffset: circumference
                }} animate={{
                  strokeDashoffset
                }} transition={{
                  duration: 1,
                  ease: 'easeOut'
                }} data-id="element-1458" />
                  </svg>
                  {/* Percentage text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1459">
                    <motion.span key={progressPct} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} className="text-5xl font-bold text-white tabular-nums" data-id="element-1460">
                      {progressPct}
                    </motion.span>
                    <span className="text-gray-500 text-sm font-medium -mt-1" data-id="element-1461">
                      %
                    </span>
                  </div>
                </div>

                {/* Progress message */}
                <motion.p key={progressPct} initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center font-heading font-semibold text-lg text-white mb-1" data-id="element-1462">
                  {getProgressMessage(progressPct, order.status)}
                </motion.p>
                <p className="text-xs text-gray-500 text-center" data-id="element-1463">
                  {isDelivered ? '¡Gracias por tu compra!' : 'Se actualiza automáticamente'}
                </p>
              </>}
          </motion.div>

          {/* Per-Station Progress Cards */}
          {!isCancelled && involvedStations.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-3" data-id="element-1464">
              <h3 className="font-heading text-sm font-semibold text-gray-400 uppercase tracking-wider px-1" data-id="element-1465">
                Progreso por estación
              </h3>

              {/* Caja / Restaurant acceptance */}
              <div className="bg-surface border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-1466">
                <div className="w-10 h-10 rounded-xl bg-[#F2C94C]/10 border border-[#F2C94C]/20 flex items-center justify-center text-lg flex-shrink-0" data-id="element-1467">
                  💰
                </div>
                <div className="flex-1 min-w-0" data-id="element-1468">
                  <p className="text-sm font-semibold text-white" data-id="element-1469">Caja</p>
                  <p className="text-xs text-gray-500" data-id="element-1470">Recepción del pedido</p>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${order.status === 'new' ? 'text-gray-500 bg-gray-500/10 border-gray-500/25' : 'text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/25'}`} data-id="element-1471">
                  {order.status === 'new' ? 'Pendiente' : '✓ Aceptado'}
                </div>
              </div>

              {/* Station cards */}
              {involvedStations.map((station, idx) => {
            const statusLabel = STATION_STATUS_LABELS[station.status] || 'Pendiente';
            const colorClass = getStatusColor(station.status);
            const weight = STATUS_WEIGHT[station.status] || 0;
            const isDone = weight >= 90;
            return <motion.div key={station.id} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.25 + idx * 0.08
            }} className="bg-surface border border-gray-800 rounded-xl p-4" data-id="element-1472">
                    <div className="flex items-center gap-3 mb-2.5" data-id="element-1473">
                      <div className="w-10 h-10 rounded-xl bg-surface-light border border-gray-700 flex items-center justify-center text-lg flex-shrink-0" data-id="element-1474">
                        {station.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1475">
                        <p className="text-sm font-semibold text-white" data-id="element-1476">
                          {station.label}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-1477">
                          {station.status === 'new' && 'Esperando...'}
                          {station.status === 'accepted' && 'Pedido recibido por la estación'}
                          {station.status === 'preparing' && 'Preparando tu pedido 🔥'}
                          {station.status === 'ready' && '¡Listo para servir!'}
                          {station.status === 'delivered' && 'Completado ✓'}
                        </p>
                      </div>
                      <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold border flex-shrink-0 ${colorClass}`} data-id="element-1478">
                        {isDone && '✓ '}
                        {statusLabel}
                      </div>
                    </div>
                    {/* Mini progress bar */}
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden" data-id="element-1479">
                      <motion.div className="h-full rounded-full" style={{
                  backgroundColor: getProgressColor(weight)
                }} initial={{
                  width: '0%'
                }} animate={{
                  width: `${weight}%`
                }} transition={{
                  duration: 0.8,
                  ease: 'easeOut'
                }} data-id="element-1480" />
                    </div>
                  </motion.div>;
          })}
            </motion.div>}

          {/* If no stations detected but order is active, show simple status */}
          {!isCancelled && involvedStations.length === 0 && order.status !== 'new' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-surface border border-gray-800 rounded-2xl p-5 shadow-lg" data-id="element-1481">
                <div className="flex items-center gap-3" data-id="element-1482">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${STATUS_CONFIG[order.status].bg}`} data-id="element-1483">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[order.status].dot} animate-pulse`} data-id="element-1484" />
                  </div>
                  <div data-id="element-1485">
                    <p className={`text-sm font-bold ${STATUS_CONFIG[order.status].color}`} data-id="element-1486">
                      {STATUS_CONFIG[order.status].label}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1487">
                      {order.status === 'accepted' && 'Orden confirmada por el restaurante'}
                      {order.status === 'preparing' && 'Cocinando tu comida 🔥'}
                      {order.status === 'ready' && (order.delivery_type === 'express' ? 'Esperando al repartidor' : '¡Listo para retirar!')}
                      {order.status === 'out_for_delivery' && 'Tu pedido está en camino 🛵'}
                    </p>
                  </div>
                </div>
              </motion.div>}

          {/* Footer */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-center text-xs text-gray-600 pt-2 pb-6" data-id="element-1488">
            Se actualiza cada 5 segundos · Tocá 🔄 para actualizar ahora
          </motion.p>
        </main>
      </div>
    </div>;
}